import { message } from 'antd'
import React, { useContext, useState } from 'react'
import { useMutation, UseMutationResult, useQueryClient } from 'react-query'
import { testFreshdeskConnection } from '../../../api/integrations'
import { useConnectedApp } from '../../ConnectedApps'
import { FreshdeskTestConnectionPayloadType } from '../definitions'

interface FreshdeskContextType {
  isSetUpOpen: boolean
  connectionPassed: boolean
  connectionFailed: boolean
  setIsSetUpOpen: React.Dispatch<React.SetStateAction<boolean>>
  setConnectionPassed: React.Dispatch<React.SetStateAction<boolean>>
  setConnectionFailed: React.Dispatch<React.SetStateAction<boolean>>
  isTestingConnection: boolean
  testFreshdeskConnectionMutation?: UseMutationResult<
    any,
    unknown,
    FreshdeskTestConnectionPayloadType,
    unknown
  >
  saveRequestTypesMutation?: UseMutationResult<
    any,
    unknown,
    FreshdeskTestConnectionPayloadType,
    unknown
  >
}

const FreshdeskContext = React.createContext<FreshdeskContextType>({
  isSetUpOpen: false,
  connectionPassed: false,
  connectionFailed: false,
  isTestingConnection: false,
  setIsSetUpOpen: () => null,
  setConnectionPassed: () => null,
  setConnectionFailed: () => null,
})

export const FreshdeskProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const queryClient = useQueryClient()
  const { isEdition } = useConnectedApp()
  const [connectionPassed, setConnectionPassed] = useState<boolean>(false)
  const [connectionFailed, setConnectionFailed] = useState<boolean>(false)
  const [isSetUpOpen, setIsSetUpOpen] = useState<boolean>(true)

  // Test Freshdesk configuration
  const testFreshdeskConnectionMutation = useMutation(testFreshdeskConnection, {
    onSuccess: () => {
      setConnectionPassed(true)
      setConnectionFailed(false)
    },
    onError: () => {
      setConnectionPassed(false)
      setConnectionFailed(true)
    },
  })

  // Added same mutation as testFreshdeskConnectionMutation to avoid unnecessary checks and balances
  const saveRequestTypesMutation = useMutation(testFreshdeskConnection, {
    onSuccess: () => {
      setIsSetUpOpen(false)
      setConnectionPassed(true)
      setConnectionFailed(false)
      message.success('Freshdesk configuration saved')
      queryClient.invalidateQueries(['fetch-freshdesk-integration'])
    },
    onError: () => {
      setConnectionPassed(false)
      setConnectionFailed(true)
    },
  })

  return (
    <FreshdeskContext.Provider
      value={{
        isSetUpOpen,
        connectionPassed,
        connectionFailed,
        isTestingConnection: testFreshdeskConnectionMutation?.isLoading,
        setIsSetUpOpen,
        setConnectionPassed,
        setConnectionFailed,
        testFreshdeskConnectionMutation,
        saveRequestTypesMutation,
      }}
    >
      {children}
    </FreshdeskContext.Provider>
  )
}

export const useFreshdesk = () => {
  return useContext(FreshdeskContext)
}
